/**
 * GA4 Ecommerce DataLayer — view_item_list & select_item
 *
 * - view_item_list: fires when a .oferta--card enters the viewport (once per card)
 * - select_item   : fires on click of any .oferta--card link
 *
 * Card data is read from the [data-ga-item] JSON attribute set server-side by PHP.
 */

(function () {
  'use strict';

  // Group cards that enter together in the same frame/scroll gesture
  var pendingItems = [];
  var flushTimer = null;

  /** Push a payload to the dataLayer, resetting ecommerce first */
  function dlPush(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
  }

  /** Parse the data-ga-item attribute from a card element */
  function parseCardData(card) {
    try {
      return JSON.parse(card.dataset.gaItem || '{}');
    } catch (e) {
      return {};
    }
  }

  /**
   * Build the item_list context for the current page.
   * Returns { item_list_id, item_list_name }
   */
  function getListContext() {
    var meta = window._rfGAListMeta || {};
    return {
      item_list_id: meta.id || 'listado_generico',
      item_list_name: meta.name || 'Listado Renting',
    };
  }

  function sumPrices(items) {
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      total += parseFloat(items[i].price) || 0;
    }
    return Math.round(total * 100) / 100;
  }

  /** Flush any pending items to the dataLayer */
  function flush() {
    if (!pendingItems.length) return;
    var ctx = getListContext();
    var items = pendingItems.slice();
    pendingItems = [];

    var totalValue = sumPrices(items);

    dlPush({
      event: 'view_item_list',
      ecommerce: {
        currency: 'EUR',
        value: totalValue,
        item_list_id: ctx.item_list_id,
        item_list_name: ctx.item_list_name,
        items: items,
      },
    });
  }

  /* ── sessionStorage Deduplication Cache ───────────────────────────── */

  var viewedItemsCache = {};

  function initViewedItemsCache() {
    try {
      var viewed = sessionStorage.getItem('rf_viewed_items');
      if (viewed) {
        var list = JSON.parse(viewed);
        if (Array.isArray(list)) {
          list.forEach(function (id) {
            viewedItemsCache[id] = true;
          });
        }
      }
    } catch (e) {
      // Ignore browser privacy/security restrictions
    }
  }

  function isItemViewed(itemId) {
    return !!viewedItemsCache[itemId];
  }

  function markItemAsViewed(itemId) {
    if (viewedItemsCache[itemId]) return;
    viewedItemsCache[itemId] = true;
    try {
      var viewed = sessionStorage.getItem('rf_viewed_items');
      var list = viewed ? JSON.parse(viewed) : [];
      if (!Array.isArray(list)) list = [];
      if (list.indexOf(itemId) === -1) {
        list.push(itemId);
        sessionStorage.setItem('rf_viewed_items', JSON.stringify(list));
      }
    } catch (e) {
      // Ignore
    }
  }

  // Initialize cache immediately
  initViewedItemsCache();

  /* ── view_item_list via IntersectionObserver ──────────────────────── */

  var seenCards = new WeakSet();
  var cardObserver = null;

  function getCardObserver() {
    if (!cardObserver) {
      cardObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var card = entry.target;
          if (seenCards.has(card)) return;
          seenCards.add(card);
          cardObserver.unobserve(card);

          var data = parseCardData(card);
          if (!data.item_id) return;

          // Deduplication per session
          if (isItemViewed(data.item_id)) {
            return;
          }
          markItemAsViewed(data.item_id);

          // Add current index position
          var siblings = Array.from(document.querySelectorAll('.oferta--card[data-ga-item]'));
          data.index = siblings.indexOf(card) + 1;

          pendingItems.push(data);
          clearTimeout(flushTimer);
          flushTimer = setTimeout(flush, 800); // Debounce to group scroll actions
        });
      }, { threshold: 0.3 });
    }
    return cardObserver;
  }

  function observeCards() {
    var cards = document.querySelectorAll('.oferta--card[data-ga-item]');
    if (!cards.length) {
      return;
    }

    var observer = getCardObserver();
    cards.forEach(function (card) {
      if (!seenCards.has(card)) {
        observer.observe(card);
      }
    });
  }

  /* ── MutationObserver for dynamic AJAX / infinite scroll cards ─────── */

  function initDynamicCardsObserver() {
    if (typeof MutationObserver === 'undefined') return;
    var debounceTimer = null;
    var target = document.getElementById('list_cars') || document.body;
    var mo = new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(observeCards, 200);
    });
    mo.observe(target, { childList: true, subtree: true });
  }

  /* ── select_item via click delegation ────────────────────────────── */

  function initSelectItem() {

    document.addEventListener('click', function (e) {
      var card = e.target.closest('.oferta--card[data-ga-item]');
      if (!card) return;

      var data = parseCardData(card);
      if (!data.item_id) return;

      var siblings = Array.from(document.querySelectorAll('.oferta--card[data-ga-item]'));
      data.index = siblings.indexOf(card) + 1;

      var ctx = getListContext();

      // Flush pending view_item_list events first to preserve sequence
      flush();

      var itemPrice = parseFloat(data.price) || 0;

      dlPush({
        event: 'select_item',
        ecommerce: {
          currency: 'EUR',
          value: itemPrice,
          item_list_id: ctx.item_list_id,
          item_list_name: ctx.item_list_name,
          items: [data],
        },
      });
    });
  }

  /* ── Page unload/visibility listeners to prevent data loss ────────── */

  window.addEventListener('pagehide', flush);
  window.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  });

  /* ── Init ─────────────────────────────────────────────────────────── */

  function init() {
    observeCards();
    initSelectItem();
    initDynamicCardsObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();